import { DOCUMENT, Location, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  LOCALE_ID,
  Optional,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  ActivationStart,
  NavigationEnd,
  Router,
} from '@angular/router';
import { ImageRef } from '@app/services/api/api.service';
import { ORIGIN } from '@app/interceptors/tokens';
import { Subject, filter, take } from 'rxjs';

@Component({
  selector: 'app-seo',
  standalone: true,
  templateUrl: './seo.component.html',
  styleUrl: './seo.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeoComponent {
  private localeMap: Record<string, any> = {
    en: {
      suffix: '',
    },
    es: {
      suffix: 'es',
    },
    de: {
      suffix: 'de',
    },
    it: {
      suffix: 'it',
    },
    fr: {
      suffix: 'fr',
    },
    pt: {
      suffix: 'pt',
    },
  };

  private destroyed = new Subject<void>();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    @Inject(PLATFORM_ID) private platformId: string,
    @Optional() @Inject(ORIGIN) private origin: string | null,
    private titleService: Title,
    private metaService: Meta,
    @Inject(LOCALE_ID) private locale: string
  ) {}

  changeLink(id: string, newHref: string) {
    const link = this.document.getElementById(id) as HTMLLinkElement;
    if (link) {
      link.href = newHref; // Direct manipulation, since it's a simple attribute change
    }
  }

  public getOrigin() {
    if (typeof window !== 'undefined') {
      return window?.location?.origin;
    } else {
      return 'https://hostelhop.io';
    }
  }

  private processRouteData(route: ActivatedRoute) {
    // Get the deepest child of the activated route
    while (route.firstChild) {
      route = route.firstChild;
    }
    const snapshot: ActivatedRouteSnapshot = route.snapshot;
    const data = snapshot.data;

    if (data.hasOwnProperty('title')) {
      const title = data['title'];
      if (data['name'] === 'hostel') {
        const hostel = data['hostel']?.data;
        if (hostel) {
          let countryOrUsState: string;
          if (hostel.address.country === 'United States') {
            countryOrUsState = hostel.address.state;
          } else {
            countryOrUsState = hostel.address.country;
          }

          let modifiedTitle = title;

          if (isPlatformBrowser(this.platformId)) {
            modifiedTitle = title
              .replace(/\[\[SSR_DATA_HOSTEL_NAME\]\]/g, hostel.name)
              .replace(/\[\[SSR_DATA_CITY_NAME\]\]/g, hostel.address.city)
              .replace(
                /\[\[SSR_DATA_COUNTRY_OR_US_STATE_NAME\]\]/g,
                countryOrUsState
              );
          }

          this.titleService.setTitle(modifiedTitle);

          const coverImage = hostel.propertyImages.find(
            (image: ImageRef) => image.isCoverImage
          );
          const src = coverImage?.fileName ?? hostel.propertyImages[0].fileName;

          if (data.hasOwnProperty('metaTags')) {
            Object.entries(data['metaTags']).forEach((tag: any) => {
              if (isPlatformBrowser(this.platformId)) {
                this.metaService.updateTag({
                  name: tag[0],
                  content: tag[1]
                    .replace(/\[\[SSR_DATA_HOSTEL_NAME\]\]/g, hostel.name)
                    .replace(/\[\[SSR_DATA_CITY_NAME\]\]/g, hostel.address.city)
                    .replace(
                      /\[\[SSR_DATA_COUNTRY_OR_US_STATE_NAME\]\]/g,
                      countryOrUsState
                    )
                    .replace(
                      /\[\[SSR_DATA_DESCRIPTION\]\]/g,
                      hostel.description
                    )
                    .replace(
                      /\[\[SSR_DATA_HOSTEL_OG_IMAGE\]\]/g,
                      `https://storage.hostelhop.io/images/resize/${src}?width=350`
                    ),
                });
              } else {
                this.metaService.updateTag({
                  name: tag[0],
                  content: tag[1]
                    .replace(/\[\[SSR_DATA_HOSTEL_NAME\]\]/g, hostel.name)
                    .replace(/\[\[SSR_DATA_CITY_NAME\]\]/g, hostel.address.city)
                    .replace(
                      /\[\[SSR_DATA_COUNTRY_OR_US_STATE_NAME\]\]/g,
                      countryOrUsState
                    )
                    .replace(
                      /\[\[SSR_DATA_DESCRIPTION\]\]/g,
                      hostel.description
                    )
                    .replace(
                      /\[\[SSR_DATA_HOSTEL_OG_IMAGE\]\]/g,
                      `https://storage.hostelhop.io/images/resize/${src}?width=350`
                    ),
                });
              }
            });
          }
        }
      } else if (data['name'] === 'city') {
        // cities have no image yet
        this.metaService.removeTag("name='og:image'");

        const city = snapshot.params['slug'].split('-').slice(0, -1).join('-');
        const countryOrUsState = snapshot.params['slug']
          .split('-')
          .slice(-1)
          .join('');

        let modifiedTitle = title;

        if (isPlatformBrowser(this.platformId)) {
          modifiedTitle = title
            .replace(/\[\[SSR_DATA_CITY_NAME\]\]/g, city)
            .replace(
              /\[\[SSR_DATA_COUNTRY_OR_US_STATE_NAME\]\]/g,
              countryOrUsState
            );
        }

        this.titleService.setTitle(modifiedTitle);

        Object.entries(data['metaTags']).forEach((tag: any) => {
          if (isPlatformBrowser(this.platformId)) {
            this.metaService.updateTag({
              name: tag[0],
              content: tag[1]
                .replace(/\[\[SSR_DATA_CITY_NAME\]\]/g, city)
                .replace(
                  /\[\[SSR_DATA_COUNTRY_OR_US_STATE_NAME\]\]/g,
                  countryOrUsState
                ),
            });
          } else {
            this.metaService.updateTag({
              name: tag[0],
              content: tag[1],
            });
          }
        });
      } else {
        this.titleService.setTitle(title);

        if (data.hasOwnProperty('metaTags')) {
          Object.entries(data['metaTags']).forEach((tag: any) => {
            this.metaService.updateTag({ name: tag[0], content: tag[1] });
          });

          if (!data['metaTags']['og:image']) {
            this.metaService.removeTag("name='og:image'");
          }
        }
      }
    }
  }

  private initFontPreload() {
    const fontPreloadLink = this.document.getElementById('font-preload');

    if (fontPreloadLink) {
      let weight;
      if (this.location.path().match(/hostels\/0000000000/)) {
        weight = 700;
      } else {
        weight = 600;
      }
      this.renderer.setAttribute(
        fontPreloadLink,
        'href',
        `assets/fonts/lexend-deca-v21-latin-${weight}.woff2`
      );
    }
  }

  ngOnInit() {
    this.initCanonicalUrl();
    this.initAlternateUrls();
    this.initFontPreload();
    // this.deleteAlternateHrefForCurrentLang();

    if (isPlatformBrowser(this.platformId)) {
      this.router.events.subscribe((data) => {
        if (data instanceof ActivationStart) {
          this.updateCanonicalHref(data.snapshot);
          const links = this.document.querySelectorAll('link[hreflang]');
          links.forEach((link: any) => {
            const newHref = this.getNewHref(link, data.snapshot);
            this.renderer.setAttribute(link, 'href', newHref);
          });
        }
      });
    }

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.processRouteData(this.route.root);
      });
  }

  private getNewHref(link: any, snapshot: ActivatedRouteSnapshot) {
    if (this.localeMap[link.hreflang as string]) {
      const suffix = (snapshot as any)._routerState.url.split('?')[0].slice(1);
      const localeSegment = this.localeMap[link.hreflang as string].suffix;
      let newHref = `${this.getOrigin()}/${
        localeSegment ? localeSegment + '/' : ''
      }${suffix}`;
      if (newHref.endsWith('/') && !localeSegment) {
        newHref = newHref.slice(0, -1);
      }
      return newHref;
    }
    return link.href;
  }

  private initCanonicalUrl() {
    let originalCanonical: string | undefined;
    if (isPlatformBrowser(this.platformId)) {
      originalCanonical = this.location.path().split('?')[0];
    } else {
      if (this.location.path() && !this.location.path().match(/0000000000/)) {
        originalCanonical = `https://hostelhop.io${
          this.locale === 'en' ? '' : '/' + this.locale
        }${this.location.path()}`;
      }
    }
    if (!originalCanonical) {
      return;
    }
    // const originalCanonical = this.location.path().split('?')[0];
    const canonicalLink = this.document.querySelector('link[rel="canonical"]');
    this.renderer.setAttribute(canonicalLink, 'href', originalCanonical);

    this.metaService.updateTag({
      name: 'og:url',
      content: originalCanonical,
    });
  }

  private initAlternateUrls() {
    let suffix: string | undefined;
    if (isPlatformBrowser(this.platformId)) {
      suffix = this.location.path().split('?')[0].slice(1);
    } else {
      if (!this.location.path().match(/0000000000/)) {
        suffix = this.location.path().split('?')[0].slice(1);
      }
    }
    const links = this.document.querySelectorAll('link[hreflang]');
    links.forEach((link: any) => {
      if (!suffix) {
        return;
      }
      if (this.localeMap[link.hreflang as string]) {
        // const suffix = (data.snapshot as any)._routerState.url.split('?')[0].slice(1);
        const localeSegment = this.localeMap[link.hreflang as string].suffix;
        let newHref = `${this.getOrigin()}/${
          localeSegment ? localeSegment + '/' : ''
        }${suffix}`;
        if (newHref.endsWith('/') && !localeSegment) {
          newHref = newHref.slice(0, -1);
        }
        this.renderer.setAttribute(link, 'href', newHref);
      }
    });
  }

  private updateCanonicalHref(snapshot: ActivatedRouteSnapshot) {
    const suffix = (snapshot as any)._routerState.url.split('?')[0].slice(1);
    const locale = this.locale;
    let localeSegment;
    if (locale === 'en') {
      localeSegment = '';
    } else {
      localeSegment = `${locale}/`;
    }

    let newHref = `${this.getOrigin()}/${
      localeSegment ? localeSegment : ''
    }${suffix}`;
    if (newHref.endsWith('/') && !localeSegment) {
      newHref = newHref.slice(0, -1);
    }

    const canonicalLink = this.document.querySelector('link[rel="canonical"]');
    this.renderer.setAttribute(canonicalLink, 'href', newHref);

    this.metaService.updateTag({
      name: 'og:url',
      content: newHref,
    });
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }
}
